import React, { useContext, useEffect, useRef, useState } from "react";
import DraftsCredentialTemplateCard from "../../components/Cards/DraftsCredentialTemplateCard/DraftsCredentialTemplateCard";
import { GET, PUT } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import AuthContext from "../../store/auth-context";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import { useLocation, useNavigate } from "react-router-dom";
import CalculateCardsPerRow from "../../service/CalculateCardsPerRow";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import { fetchOrganisationsAPI } from "../../common/Organisations/fetchOrganisations";
import { fetchCurrentUser } from "../../service/CurrentUser";
import { useTranslation } from "react-i18next";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const Draft = () => {
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const { t } = useTranslation();

  const [isLoadingCredTemplates, setIsLoadingCredTemplates] = useState(true);
  const [cardWidth] = useState("19.125rem");
  const [gap] = useState("1.5rem");
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(0);
  const [countRecentList, setCountRecentList] = useState(0);
  const [countLast30DaysList, setCountLast30DaysList] = useState(0);
  const [countLast3MonthsList, setCountLast3MonthsList] = useState(0);
  const [countLast6MonthsList, setCountLast6MonthsList] = useState(0);
  const [countOtherList, setCountOtherList] = useState(0);
  const [recentList, setRecentList] = useState([]);
  const [last30DaysList, setLast30DaysList] = useState([]);
  const [last3MonthsList, setLast3MonthsList] = useState([]);
  const [last6MonthsList, setLast6MonthsList] = useState([]);
  const [otherList, setOtherList] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrganisation, setSelectedOrganisation] = useState("");
  const [orgCode, setOrgCode] = useState("");
  const [userType, setUserType] = useState(null);
  const [tenantCode, setTenantCode] = useState("");

  const [organisationOptions, setOrganisationOptions] = useState([
    { name: "", value: "" },
  ]);
  const [orgNext, setOrgNext] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgOffset, setOrgOffset] = useState(0);
  const [hasOrganisations, setHasOrganisations] = useState(false);
  const orgDropdownRef = useRef(null);
  const normalizedUserType = (userType ?? "").toLowerCase();

  const navigate = useNavigate();

  const buildDraftsURL = (base) => {
    return orgCode ? `${base}&org_code=${orgCode}` : base;
  };

  const fetchOrgs = async (loadMore = false) => {
    try {
      setOrgLoading(true);
      const offset = loadMore ? orgOffset : 0;
      const data = await fetchOrganisationsAPI(tenantCode, offset);

      if (!loadMore && data.options.length <= 1) {
        setHasOrganisations(false);
        setOrganisationOptions([
          { name: t("drafts:all_organisations"), value: "" },
        ]);
      } else {
        setHasOrganisations(true);
        if (loadMore) {
          const newOptions = data.options.filter((opt) => opt.value !== "");
          setOrganisationOptions((prev) => [...prev, ...newOptions]);
        } else {
          setOrganisationOptions(data.options);
        }
      }

      setOrgNext(data.next);
      setOrgOffset(
        offset + data.options.filter((opt) => opt.value !== "").length,
      );
    } catch (error) {
      setHasOrganisations(false);
      Toast({
        type: "error",
        title: t("drafts:error"),
        message: t("drafts:failed_fetch_organisations"),
      });
    } finally {
      setOrgLoading(false);
    }
  };

  const handleOrgScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      orgNext &&
      !orgLoading
    ) {
      fetchOrgs(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(event.target)
      ) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!orgDropdownOpen) {
      setOrgOffset(0);
    }
  }, [orgDropdownOpen]);

  useEffect(() => {
    const loadData = async () => {
      const data = await fetchCurrentUser();
      if (data) {
        setUserType(data.type);
        setTenantCode(data.tenant_code);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (
      tenantCode &&
      (normalizedUserType === "tenant_admin" ||
        normalizedUserType === "tenant_user")
    ) {
      fetchOrgs();
    }
  }, [tenantCode]);

  useEffect(() => {
    if (cardsPerRow > 0) {
      loadAllDraftTemplates();
    }
  }, [orgCode, cardsPerRow]);

  const loadAllDraftTemplates = () => {
    setIsLoadingCredTemplates(true);
    setInitialLoadCompleted(false);

    setRecentList([]);
    setLast30DaysList([]);
    setLast3MonthsList([]);
    setLast6MonthsList([]);
    setOtherList([]);

    Promise.all([
      GET_ISSUER_RECENT(cardsPerRow, 0, 0, 7),
      GET_ISSUER_LAST_30_DAYS(cardsPerRow, 0, 7, 30),
      GET_ISSUER_LAST_3_MONTHS(cardsPerRow, 0, 30, 90),
      GET_ISSUER_LAST_6_MONTHS(cardsPerRow, 0, 90, 180),
      GET_ISSUER_LAST_OTHERS(cardsPerRow, 0, 180, 365),
    ])
      .then(() => {
        setIsLoadingCredTemplates(false);
        setInitialLoadCompleted(true);
      })
      .catch((error) => {
        console.error("Error loading certificates:", error);
        setIsLoadingCredTemplates(false);
        setInitialLoadCompleted(true);
      });
  };

  const performSearchAPI = async (searchTerm) => {
    try {
      const url = buildDraftsURL(
        `${EntityConstants.GET_DRAFTS_TEMPLATES}search=${searchTerm}`,
      );
      const response = await GET(url);
      const data = await response.json();

      if (data.error) {
        console.log(data.error);
        setSearchResults([]);
      } else {
        setSearchResults(data.results);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
        setSearchResults([]);
      }
    }
  };

  const { handleSearch } = useSearchDebounce(performSearchAPI, 500);

  const handleSearchInputChange = (e) => {
    const term = e.target.value ? e.target.value : "";
    setSearchTerm(term);

    if (term === "") {
      setIsSearching(false);
      setSearchResults([]);
      setSearchCompleted(false);
      return;
    }
    setIsSearching(true);
    setSearchCompleted(false);
    handleSearch(term, {
      onStart: () => setIsSearching(true),
      onComplete: () => {
        setIsSearching(false);
        setSearchCompleted(true);
      },
      onError: () => {
        setSearchResults([]);
        setSearchCompleted(true);
        setIsSearching(false);
      },
      minChars: 1,
    });
  };

  const GET_ISSUER_RECENT = (limit, offset, startDay, endDay) => {
    const url = buildDraftsURL(
      `${EntityConstants.GET_DRAFTS_TEMPLATES}limit=${limit}&offset=${offset}&start_day=${startDay}&end_day=${endDay}`,
    );
    return GET(url)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
          return { error: true };
        } else {
          setCountRecentList(data.count);
          setRecentList((perv) => [...perv, ...data.results]);
          return { success: true, count: data.count };
        }
      })
      .catch((error) => {
        console.error(error);
        return { error: true };
      });
  };

  const GET_ISSUER_LAST_30_DAYS = (limit, offset, startDay, endDay) => {
    const url = buildDraftsURL(
      `${EntityConstants.GET_DRAFTS_TEMPLATES}limit=${limit}&offset=${offset}&start_day=${startDay}&end_day=${endDay}`,
    );
    return GET(url)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
          return { error: true };
        } else {
          setCountLast30DaysList(data.count);
          setLast30DaysList((perv) => [...perv, ...data.results]);
          return { success: true, count: data.count };
        }
      })
      .catch((error) => {
        console.error(error);
        return { error: true };
      });
  };

  const GET_ISSUER_LAST_3_MONTHS = (limit, offset, startDay, endDay) => {
    const url = buildDraftsURL(
      `${EntityConstants.GET_DRAFTS_TEMPLATES}limit=${limit}&offset=${offset}&start_day=${startDay}&end_day=${endDay}`,
    );
    return GET(url)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
          return { error: true };
        } else {
          setCountLast3MonthsList(data.count);
          setLast3MonthsList((perv) => [...perv, ...data.results]);
          return { success: true, count: data.count };
        }
      })
      .catch((error) => {
        console.error(error);
        return { error: true };
      });
  };

  const GET_ISSUER_LAST_6_MONTHS = (limit, offset, startDay, endDay) => {
    const url = buildDraftsURL(
      `${EntityConstants.GET_DRAFTS_TEMPLATES}limit=${limit}&offset=${offset}&start_day=${startDay}&end_day=${endDay}`,
    );
    return GET(url)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
          return { error: true };
        } else {
          setCountLast6MonthsList(data.count);
          setLast6MonthsList((perv) => [...perv, ...data.results]);
          return { success: true, count: data.count };
        }
      })
      .catch((error) => {
        console.error(error);
        return { error: true };
      });
  };

  const GET_ISSUER_LAST_OTHERS = (limit, offset, startDay, endDay) => {
    const url = buildDraftsURL(
      `${EntityConstants.GET_DRAFTS_TEMPLATES}limit=${limit}&offset=${offset}&start_day=${startDay}&end_day=${endDay}`,
    );
    return GET(url)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
          return { error: true };
        } else {
          setCountOtherList(data.count);
          setOtherList((perv) => [...perv, ...data.results]);
          return { success: true, count: data.count };
        }
      })
      .catch((error) => {
        console.error(error);
        return { error: true };
      });
  };

  const removeCredentialFromList = async (credentialId) => {
    try {
      const response = await PUT(
        `${EntityConstants.PUT_REVOKE_DRAFT}?issuer_credential_template_id=${credentialId}`,
      );
      const data = await response.json();

      if (response.ok) {
        const removeAndEnsure = (list, setList, startDay, endDay, count) => {
          setList((prev) => {
            const updatedList = prev.filter(
              (item) => item.issuer_credential_template_id !== credentialId,
            );
            ensureFullRow(updatedList, setList, startDay, endDay, count);
            return updatedList;
          });
        };

        const ensureFullRow = (list, setList, startDay, endDay, count) => {
          if (list.length < cardsPerRow && count > list.length) {
            const offset = list.length;
            const url = buildDraftsURL(
              `${EntityConstants.GET_DRAFTS_TEMPLATES}limit=${
                cardsPerRow - list.length
              }&offset=${offset}&start_day=${startDay}&end_day=${endDay}`,
            );
            return GET(url)
              .then((response) => response.json())
              .then((data) => {
                if (!data.error && data.results.length > 0) {
                  setList((prev) => [...prev, ...data.results]);
                }
              })
              .catch((error) => {
                console.error("Error fetching additional certificates:", error);
              });
          }
        };

        removeAndEnsure(searchResults, setSearchResults);
        removeAndEnsure(recentList, setRecentList, 0, 7, countRecentList);
        removeAndEnsure(
          last30DaysList,
          setLast30DaysList,
          7,
          30,
          countLast30DaysList,
        );
        removeAndEnsure(
          last3MonthsList,
          setLast3MonthsList,
          30,
          90,
          countLast3MonthsList,
        );
        removeAndEnsure(
          last6MonthsList,
          setLast6MonthsList,
          90,
          180,
          countLast6MonthsList,
        );
        removeAndEnsure(otherList, setOtherList, 180, 365, countOtherList);

        Toast({
          type: "success",
          title: t("drafts:success"),
          message: `${data.details}`,
        });
      } else {
        Toast({
          type: "error",
          title: t("drafts:error"),
          message: `${data.error}`,
        });
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("drafts:error"),
        message: t("drafts:error_deleting_draft"),
      });
    }
  };

  useEffect(() => {
    const updateCardsPerRow = () => {
      if (containerRef.current) {
        const calculatedCardsPerRow = CalculateCardsPerRow(
          containerRef,
          cardWidth,
          gap,
        );
        if (calculatedCardsPerRow < 5) {
          setCardsPerRow(4);
        } else {
          setCardsPerRow(calculatedCardsPerRow);
        }
      }
    };

    updateCardsPerRow();
  }, [cardWidth, gap]);

  useEffect(() => {
    authContext.lastVisitedPathHandler(location.pathname);
  }, []);

  const hasCertificates = () => {
    return (
      recentList.length > 0 ||
      last30DaysList.length > 0 ||
      last3MonthsList.length > 0 ||
      last6MonthsList.length > 0 ||
      otherList.length > 0
    );
  };

  return (
    <div className="flex flex-col items-center w-full mt-20 bg-cfSecondary min-h-screen" id={pageRootTestIds.drafts}>
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <div className="flex items-center gap-4">
            <p className="text-cfHeadingText font-semibold self-start my-auto">
              {t("drafts:draft_templates")}
            </p>
            {(normalizedUserType === "tenant_admin" ||
              normalizedUserType === "tenant_user") &&
              hasOrganisations && (
                <div className="relative" ref={orgDropdownRef}>
                  <div
                    id="drafts-dropdown-organisation-trigger"
                    className="flex justify-between items-center border-[1px] rounded-md p-[0.52rem] text-sm w-full cursor-pointer min-w-[200px] bg-white"
                    onClick={() => {
                      setOrgDropdownOpen(!orgDropdownOpen);
                      if (!orgDropdownOpen) {
                        fetchOrgs();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setOrgDropdownOpen(!orgDropdownOpen);
                        if (!orgDropdownOpen) {
                          fetchOrgs();
                        }
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="truncate">
                      {selectedOrganisation
                        ? organisationOptions.find(
                            (opt) => opt.value === selectedOrganisation,
                          )?.name
                        : t("drafts:all_organisations")}
                    </span>
                    <svg
                      className={`w-5 h-5 ml-2 transition-transform duration-200 ${
                        orgDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {orgDropdownOpen && (
                    <div
                      id="drafts-dropdown-organisation-menu"
                      className="absolute z-20 mt-1 w-full max-h-60 overflow-auto border border-gray-300 bg-white rounded-md shadow-lg"
                      onScroll={handleOrgScroll}
                    >
                      {organisationOptions.length > 0 ? (
                        organisationOptions.map((option) => (
                          <div
                            key={option.value}
                            id={`drafts-dropdown-organisation-option-${option.value || "all"}`}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer truncate"
                            onClick={() => {
                              setSelectedOrganisation(option.value);
                              setOrgCode(option.value);
                              setOrgDropdownOpen(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                setSelectedOrganisation(option.value);
                                setOrgCode(option.value);
                                setOrgDropdownOpen(false);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {option.name || t("drafts:all_organisations")}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {orgLoading
                            ? t("drafts:loading")
                            : t("drafts:no_organisations_found")}
                        </div>
                      )}
                      {orgLoading && (
                        <div className="px-4 py-2 flex justify-center">
                          {t("drafts:loading")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
          </div>
          {/* Search */}
          <form
            onSubmit={(e) => e.preventDefault()}
            className="bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center ml-[0rem] md:ml-[0.630rem] md:self-end md:m-0"
          >
            <input
              type="text"
              name="search"
              id="drafts-input-search"
              value={searchTerm}
              onChange={handleSearchInputChange}
              placeholder={t("drafts:search_drafts")}
              className="outline-none border-none p-0 placeholder:text-sm w-full"
              style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
            />
          </form>
        </div>
      </div>

      <div ref={containerRef} className="w-full px-4 pb-4">
        {searchTerm && (isSearching || searchCompleted) ? (
          <div className="flex items-center gap-6 flex-wrap w-full mt-8">
            {isSearching && searchResults.length === 0 && (
              <div className="w-full flex justify-center items-center">
                <CfLoader />
              </div>
            )}

            {searchResults.map(
              ({
                issuer_credential_template_id,
                credential_template_id,
                certificate_name,
                thumbnail_webp_url,
                created_at,
                updated_at,
                description,
                tagsList,
                template_type,
                programme_name,
                programme_code,
                batch_name,
                batch_code,
                thumbnail_image,
              }) => (
                <DraftsCredentialTemplateCard
                  key={issuer_credential_template_id}
                  name={certificate_name}
                  credential_template_id={credential_template_id}
                  issuer_credential_template_id={issuer_credential_template_id}
                  created_at={created_at}
                  lastEdited={updated_at}
                  thumbnail_webp_url={thumbnail_webp_url}
                  description={description}
                  tagsList={tagsList}
                  navigate_url={`/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`}
                  removeCredentialFromList={removeCredentialFromList}
                  template_type={template_type}
                  programme_name={programme_name}
                  programme_code={programme_code}
                  batch_name={batch_name}
                  batch_code={batch_code}
                  thumbnail_image={thumbnail_image}
                />
              ),
            )}

            {searchCompleted && searchResults.length === 0 && (
              <div className="mt-6 w-full flex justify-center">
                <p
                  id="drafts-search-no-results"
                  className="text-lg font-semibold text-gray-600"
                >
                  {t("drafts:certificate_not_found")}
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            {isLoadingCredTemplates && (
              <div className="w-full flex justify-center items-center py-8">
                <CfLoader />
              </div>
            )}

            {!isLoadingCredTemplates && initialLoadCompleted && (
              <>
                {recentList[0] && (
                  <div className="flex justify-between my-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <p className="text-cfHeadingText font-semibold text-lg">
                        {t("drafts:recent")}
                      </p>
                      <p className="text-cfTotalCertificates">
                        ({countRecentList})
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        navigate(
                          `/issuer/drafts/timestamp/${countRecentList}/0/7/recent`,
                        )
                      }
                      id="drafts-view-all-recent"
                      className="font-cfPrimaryFont text-sm font-semibold text-cfViewAllTagText cursor-pointer focus:outline-none"
                    >
                      <p>{t("drafts:view_all")}</p>
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-6 flex-wrap w-full">
                  {recentList.map(
                    ({
                      issuer_credential_template_id,
                      credential_template_id,
                      certificate_name,
                      thumbnail_webp_url,
                      created_at,
                      updated_at,
                      description,
                      tagsList,
                      template_type,
                      programme_name,
                      programme_code,
                      batch_name,
                      batch_code,
                      thumbnail_image,
                    }) => (
                      <DraftsCredentialTemplateCard
                        key={issuer_credential_template_id}
                        name={certificate_name}
                        credential_template_id={credential_template_id}
                        issuer_credential_template_id={
                          issuer_credential_template_id
                        }
                        created_at={created_at}
                        lastEdited={updated_at}
                        thumbnail_webp_url={thumbnail_webp_url}
                        description={description}
                        tagsList={tagsList}
                        navigate_url={`/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`}
                        removeCredentialFromList={removeCredentialFromList}
                        template_type={template_type}
                        programme_name={programme_name}
                        programme_code={programme_code}
                        batch_name={batch_name}
                        batch_code={batch_code}
                        thumbnail_image={thumbnail_image}
                      />
                    ),
                  )}
                </div>
                {last30DaysList[0] && (
                  <div className="flex justify-between my-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <p className="text-cfHeadingText font-semibold text-lg">
                        {t("drafts:last_30_days")}
                      </p>
                      <p className="text-cfTotalCertificates">
                        ({countLast30DaysList})
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        navigate(
                          `/issuer/drafts/timestamp/${countLast30DaysList}/7/30/last-30-days`,
                        )
                      }
                      id="drafts-view-all-last-30-days"
                      className="font-cfPrimaryFont text-sm font-semibold text-cfViewAllTagText cursor-pointer focus:outline-none"
                    >
                      <p>{t("drafts:view_all")}</p>
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-6 flex-wrap w-full">
                  {last30DaysList.map(
                    ({
                      issuer_credential_template_id,
                      credential_template_id,
                      certificate_name,
                      thumbnail_webp_url,
                      created_at,
                      updated_at,
                      description,
                      tagsList,
                      template_type,
                      programme_name,
                      programme_code,
                      batch_name,
                      batch_code,
                      thumbnail_image,
                    }) => (
                      <DraftsCredentialTemplateCard
                        key={issuer_credential_template_id}
                        name={certificate_name}
                        credential_template_id={credential_template_id}
                        issuer_credential_template_id={
                          issuer_credential_template_id
                        }
                        created_at={created_at}
                        lastEdited={updated_at}
                        thumbnail_webp_url={thumbnail_webp_url}
                        navigate_url={`/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`}
                        description={description}
                        tagsList={tagsList}
                        removeCredentialFromList={removeCredentialFromList}
                        template_type={template_type}
                        programme_name={programme_name}
                        programme_code={programme_code}
                        batch_name={batch_name}
                        batch_code={batch_code}
                        thumbnail_image={thumbnail_image}
                      />
                    ),
                  )}
                </div>
                {last3MonthsList[0] && (
                  <div className="flex justify-between my-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <p className="text-cfHeadingText font-semibold text-lg">
                        {t("drafts:last_3_months")}
                      </p>
                      <p className="text-cfTotalCertificates">
                        ({countLast3MonthsList})
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        navigate(
                          `/issuer/drafts/timestamp/${countLast3MonthsList}/30/90/last-3-months`,
                        )
                      }
                      id="drafts-view-all-last-3-months"
                      className="font-cfPrimaryFont text-sm font-semibold text-cfViewAllTagText cursor-pointer focus:outline-none"
                    >
                      <p>{t("drafts:view_all")}</p>
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-6 flex-wrap w-full">
                  {last3MonthsList.map(
                    ({
                      issuer_credential_template_id,
                      credential_template_id,
                      certificate_name,
                      thumbnail_webp_url,
                      created_at,
                      updated_at,
                      description,
                      tagsList,
                      template_type,
                      programme_name,
                      programme_code,
                      batch_name,
                      batch_code,
                      thumbnail_image,
                    }) => (
                      <DraftsCredentialTemplateCard
                        key={issuer_credential_template_id}
                        name={certificate_name}
                        credential_template_id={credential_template_id}
                        issuer_credential_template_id={
                          issuer_credential_template_id
                        }
                        created_at={created_at}
                        lastEdited={updated_at}
                        thumbnail_webp_url={thumbnail_webp_url}
                        navigate_url={`/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`}
                        description={description}
                        tagsList={tagsList}
                        removeCredentialFromList={removeCredentialFromList}
                        template_type={template_type}
                        programme_name={programme_name}
                        programme_code={programme_code}
                        batch_name={batch_name}
                        batch_code={batch_code}
                        thumbnail_image={thumbnail_image}
                      />
                    ),
                  )}
                </div>
                {last6MonthsList[0] && (
                  <div className="flex justify-between my-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <p className="text-cfHeadingText font-semibold text-lg">
                        {t("drafts:last_6_months")}
                      </p>
                      <p className="text-cfTotalCertificates">
                        ({countLast6MonthsList})
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        navigate(
                          `/issuer/drafts/timestamp/${countLast6MonthsList}/90/180/last-6-months`,
                        )
                      }
                      id="drafts-view-all-last-6-months"
                      className="font-cfPrimaryFont text-sm font-semibold text-cfViewAllTagText cursor-pointer focus:outline-none"
                    >
                      <p>{t("drafts:view_all")}</p>
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-6 flex-wrap w-full">
                  {last6MonthsList.map(
                    ({
                      issuer_credential_template_id,
                      credential_template_id,
                      certificate_name,
                      thumbnail_webp_url,
                      created_at,
                      updated_at,
                      description,
                      tagsList,
                      template_type,
                      programme_name,
                      programme_code,
                      batch_name,
                      batch_code,
                      thumbnail_image,
                    }) => (
                      <DraftsCredentialTemplateCard
                        key={issuer_credential_template_id}
                        name={certificate_name}
                        credential_template_id={credential_template_id}
                        issuer_credential_template_id={
                          issuer_credential_template_id
                        }
                        created_at={created_at}
                        lastEdited={updated_at}
                        thumbnail_webp_url={thumbnail_webp_url}
                        navigate_url={`/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`}
                        description={description}
                        tagsList={tagsList}
                        removeCredentialFromList={removeCredentialFromList}
                        template_type={template_type}
                        programme_name={programme_name}
                        programme_code={programme_code}
                        batch_name={batch_name}
                        batch_code={batch_code}
                        thumbnail_image={thumbnail_image}
                      />
                    ),
                  )}
                </div>
                {otherList[0] && (
                  <div className="flex justify-between my-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <p className="text-cfHeadingText font-semibold text-lg">
                        {t("drafts:others")}
                      </p>
                      <p className="text-cfTotalCertificates">
                        ({countOtherList})
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        navigate(
                          `/issuer/drafts/timestamp/${countOtherList}/180/365/others`,
                        )
                      }
                      id="drafts-view-all-others"
                      className="font-cfPrimaryFont text-sm font-semibold text-cfViewAllTagText cursor-pointer focus:outline-none"
                    >
                      <p>{t("drafts:view_all")}</p>
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-6 flex-wrap w-full">
                  {otherList.map(
                    ({
                      issuer_credential_template_id,
                      credential_template_id,
                      certificate_name,
                      thumbnail_webp_url,
                      created_at,
                      updated_at,
                      description,
                      tagsList,
                      template_type,
                      programme_name,
                      programme_code,
                      batch_name,
                      batch_code,
                      thumbnail_image,
                    }) => (
                      <DraftsCredentialTemplateCard
                        key={issuer_credential_template_id}
                        name={certificate_name}
                        credential_template_id={credential_template_id}
                        issuer_credential_template_id={
                          issuer_credential_template_id
                        }
                        created_at={created_at}
                        lastEdited={updated_at}
                        thumbnail_webp_url={thumbnail_webp_url}
                        navigate_url={`/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`}
                        description={description}
                        tagsList={tagsList}
                        removeCredentialFromList={removeCredentialFromList}
                        template_type={template_type}
                        programme_name={programme_name}
                        programme_code={programme_code}
                        batch_name={batch_name}
                        batch_code={batch_code}
                        thumbnail_image={thumbnail_image}
                      />
                    ),
                  )}
                </div>
                {!hasCertificates() && (
                  <div className="mt-6 w-full flex justify-center">
                    <p
                      id="drafts-no-certificates"
                      className="text-lg font-semibold text-gray-600"
                    >
                      {t("drafts:no_certificates_found")}
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Draft;
